import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Share2, Captions, Heart, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export const GIFT_ICON = { rose: "🌹", champagne: "🥂", doves: "🕊️", rings: "💍", fireworks: "🎆" };

export function GiftBurst({ room }) {
  const [q, setQ] = useState([]);
  useEffect(() => room?.on("gift", (g) => {
    const id = Math.random().toString(36).slice(2);
    setQ((x) => [...x, { ...g, id }]);
    setTimeout(() => setQ((x) => x.filter((i) => i.id !== id)), 4200);
  }), [room]);
  const g = q[0];
  return (
    <AnimatePresence>
      {g && (
        <motion.div key={g.id} className="absolute inset-x-0 top-[22%] z-30 flex flex-col items-center pointer-events-none" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, y: -60 }} transition={{ type: "spring", stiffness: 180, damping: 14 }} data-testid="live-gift-burst">
          <motion.span className="text-[96px] leading-none drop-shadow-[0_0_30px_rgba(200,169,107,0.9)]" animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.15, 1] }} transition={{ duration: 1.2, repeat: 2 }}>{GIFT_ICON[g.gift]}</motion.span>
          {Array.from({ length: 14 }, (_, i) => <motion.span key={i} className="absolute top-12 w-2 h-2 rounded-full bg-[var(--gold)]" initial={{ x: 0, y: 0, opacity: 1 }} animate={{ x: Math.cos(i / 14 * 6.28) * 150, y: Math.sin(i / 14 * 6.28) * 150, opacity: 0 }} transition={{ duration: 1.4 }} />)}
          <p className="mt-3 bg-black/60 backdrop-blur rounded-full px-4 py-1.5 text-sm text-white"><b className="text-[var(--gold)]">{g.name}</b> sent {g.gift_name} · ${g.amount}</p>
          {g.message && <p className="mt-1 text-xs text-white/90 bg-black/40 rounded-full px-3 py-1 max-w-xs truncate">"{g.message}"</p>}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GiftSheet({ bid, onClose }) {
  const [cat, setCat] = useState([]);
  const [pick, setPick] = useState("champagne");
  const [name, setName] = useState(localStorage.getItem("lif_name") || "");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState(localStorage.getItem("lif_email") || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/live/gifts/catalog").then((r) => setCat(r.data)).catch(() => {}); }, []);
  async function send() {
    if (!name.trim()) return toast.error("Add your name so the couple knows");
    localStorage.setItem("lif_name", name.trim());
    if (email.trim()) localStorage.setItem("lif_email", email.trim());
    setBusy(true);
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.post(`/broadcasts/${bid}/gifts/checkout`, { gift: pick, name: name.trim(), message: msg.trim(), email: email.trim() || undefined, origin_url: window.location.origin });
      if (tab) tab.location.href = data.url; else window.open(data.url, "_blank");
      toast.message("Finish paying in the new tab. Your gift pops up here once it's paid.");
      onClose();
    } catch (e) { tab?.close(); toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center" onClick={onClose}>
      <div className="w-full max-w-[520px] bg-white text-[#111] rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()} data-testid="live-gift-sheet">
        <div className="flex items-center justify-between mb-1"><p className="font-serif text-2xl">Send a gift</p><button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button></div>
        <p className="text-xs text-[var(--muted)] mb-4">It pops up on screen for everyone, and the money goes to the couple's cash fund. Test mode: use card 4242 4242 4242 4242.</p>
        <div className="grid grid-cols-5 gap-2 mb-4">
          {cat.map((g) => (
            <button key={g.id} onClick={() => setPick(g.id)} className={`rounded-2xl border py-2 flex flex-col items-center transition-colors ${pick === g.id ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)]"}`} data-testid={`live-gift-${g.id}`}>
              <span className="text-3xl">{GIFT_ICON[g.id]}</span><span className="text-[10px] mt-1 leading-tight text-center">{g.name}</span><span className="text-xs font-semibold">${g.amount}</span>
            </button>
          ))}
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Your name" className="input-luxe mb-2" data-testid="live-gift-name" />
        <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={120} placeholder="A short message (optional)" className="input-luxe mb-2" data-testid="live-gift-message" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} placeholder="Email for your receipt (optional)" className="input-luxe mb-3" data-testid="live-gift-email" />
        <button onClick={send} disabled={busy || !cat.length} className="btn-gold w-full" data-testid="live-gift-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Send {cat.find((g) => g.id === pick)?.name} · ${cat.find((g) => g.id === pick)?.amount}</button>
      </div>
    </div>,
    document.body,
  );
}

// TikTok-style quick bubbles on the right edge of every live.
export function SideBubbles({ room, bid, slug, cc, setCc }) {
  const [gift, setGift] = useState(false);
  const b = "flex flex-col items-center gap-0.5 text-[10px] text-white drop-shadow";
  const dot = "w-11 h-11 rounded-full bg-black/45 backdrop-blur flex items-center justify-center hover:bg-black/60 active:scale-90 transition-transform";
  async function share() {
    const url = `${window.location.origin}/live/${bid}`;
    try { if (navigator.share) await navigator.share({ title: "Watch live", url }); else { await navigator.clipboard.writeText(url); toast.success("Live link copied"); } } catch { /* cancelled */ }
  }
  return (
    <>
      <div className="flex flex-col gap-3" data-testid="live-side-bubbles">
        <button onClick={() => room.react("❤️")} className={b} data-testid="live-bubble-like"><span className={dot}><Heart className="w-5 h-5 fill-red-500 text-red-500" /></span>Love</button>
        {bid && slug && <button onClick={() => setGift(true)} className={b} data-testid="live-bubble-gift"><span className={`${dot} !bg-[var(--gold)] text-black`}><Gift className="w-5 h-5" /></span>Gift</button>}
        <button onClick={() => setCc(!cc)} className={b} data-testid="live-bubble-cc"><span className={`${dot} ${cc ? "!bg-white text-black" : ""}`}><Captions className="w-5 h-5" /></span>{cc ? "CC on" : "CC"}</button>
        {bid && <button onClick={share} className={b} data-testid="live-bubble-share"><span className={dot}><Share2 className="w-5 h-5" /></span>Share</button>}
        {slug && <a href={`/site/${slug}#registry`} target="_blank" rel="noreferrer" className={b} data-testid="live-bubble-registry"><span className={dot}><span className="text-lg">🎁</span></span>Registry</a>}
      </div>
      {gift && <GiftSheet bid={bid} onClose={() => setGift(false)} />}
    </>
  );
}

// Host: speech-to-text on this device, sent to everyone. Viewers: caption bar, translated to their language.
export function Captions_({ room, isStage, cc }) {
  const [line, setLine] = useState(null);
  const rec = useRef(null);
  useEffect(() => room?.on("caption", async (c) => {
    setLine({ name: c.name, text: c.text });
    const lang = localStorage.getItem("lif_lang") || "en";
    if (c.final && lang !== "en") {
      try { const { data } = await api.post("/public/translate", { lang, texts: [c.text] }); const t = data.translations[c.text]; if (t) setLine({ name: c.name, text: t }); } catch { /* keep original */ }
    }
  }), [room]);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!isStage || !cc || !SR) { if (isStage && cc && !SR) toast.message("Live captions need Chrome, Edge or Safari on this device"); return undefined; }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = navigator.language || "en-US";
    let alive = true, last = 0;
    r.onresult = (e) => {
      const res = e.results[e.results.length - 1], text = res[0].transcript.trim(), now = Date.now();
      setLine({ name: "You", text });
      if (res.isFinal || now - last > 700) { last = now; room.send({ type: "caption", text, final: res.isFinal }); }
    };
    r.onend = () => { if (alive) try { r.start(); } catch { /* restarting */ } };
    try { r.start(); } catch { /* already running */ }
    rec.current = r;
    return () => { alive = false; try { r.stop(); } catch { /* noop */ } };
  }, [isStage, cc, room]);
  useEffect(() => { if (!line) return undefined; const t = setTimeout(() => setLine(null), 5000); return () => clearTimeout(t); }, [line]);
  if (!cc || !line) return null;
  return (
    <div className="absolute inset-x-4 bottom-[150px] z-20 flex justify-center pointer-events-none" data-testid="live-caption">
      <p className="max-w-lg text-center bg-black/70 rounded-xl px-4 py-2 text-base text-white leading-snug"><span className="text-[var(--gold)] text-xs mr-1">{line.name}</span>{line.text}</p>
    </div>
  );
}
